import { ILocalization } from '../types';

export interface IResponseData {
    contextData: IContextData;
    breadcrumb: IBreadcrumbData;
    mixedMedia: IMixedMedia;
    orderModule: IOrderModuleData;
    shutterModule: IShutterModule;
    sortedBrickItems: IBrickItem[];
    translationUrls: ITranslationUrls[];
    localizationObject: ILocalization;
    category: ICategory;
}

export type ITrackingData = {
    orderModule: Pick<IOrderModuleData, 'flexSetInfo' | 'flexSetPrice'>;
    category: ICategory;
};

export interface IPiwikVisitedArticle {
    sku: string;
    name: string;
    category: string[];
    price: string;
    variant: string | null;
    brand: string;
    customDimensions: {
        '5': string;
    },
    utm_campaign: string | null,
    utm_medium: string | null,
    utm_source: string | null,
    utm_term: string | null,
    utm_id: string | null,
    testip: string | null,
    itemOrigin: string,
    quantity?: number,
}

// FlexSetPageDataDynamicPart.cs
export interface IResponseDataRefresh {
    mixedMediaForBrick: IMixedMediaOfBrick;
    orderModule: IOrderModuleDynamicPart;
    articleSetItemDescription: IArticleSetItem;
    brickItem: IBrickItem;
}

export interface IOrderModuleDynamicPart {
    price: IPrice;
    originalPrice: IPrice;
    priceDeduction: number;
    deliveryInformation: IDeliveryInformation;
    availabilityState: number;
    brickSelection: IBrickSelection;
}

export interface IBrickSelection {
    brickNo: number;
    positionNo: number
    selectedBrickItemNo: number;
    masterArticleNo: number;
    salesArticleVariantKey: number;
    modelCode: number;
    salesDesignation: string;
    imageFullPath: string;
    colorText: string;
    sizeText: string;
    modelText: string;
    amountOfItems: number;
    isFreeInSet: boolean;
    mainNavigationKeyPath: string;
    includedQuantity: number;
}

export interface IBrickItem {
    brickItemNo: number;
    brickNo: number;
    fsCode: string; // unique and the same for all brick items
    isBrickItemSelected: boolean;
    masterArticleNo: number;
    salesDesignation: string;
    imageFullPath: string;
    selectedSavKey: string;
    selectedColor?: IColor;
    selectedModel?: IModel;
    selectedSize?: ISize;
    selectedSizeType?: string;
    variantSelection: IVariantSelection;
    articleState: string;
    mainNavigationKeyPath: string;
    includedQuantity: number;
    isFreeInSet: boolean;
    setItem?: IArticleSetItem
    deliveryInformation: IDeliveryInformation;
    availabilityState: number;
}

export interface IVariantSelection {
    availableColors: IAvailableColor[];
    availableSizeTypes: ISizeType[];
    availableModels: IAvailableModel[];
    showSizeSwitcher: boolean;
    showColorSwitcher: boolean;
    showModelSwitcher: boolean;
    showSizeTypesAsTabs: boolean;
    isDefaultSize: boolean;
    sizeTableType: string;
    isScrew: boolean;
}

export enum NodeType {
    Paragraph,
    Text,
    LineBreak,
    List,
    ListItem,
    BlankLine
}

export enum PiwikEvent {
    EcommerceProductDetailView = 'ecommerceProductDetailView',
    EcommerceRemoveFromCart = 'ecommerceRemoveFromCart',
}

export enum MetaEvent {
    ViewContent = 'ViewContent',
}

export interface IVariantChangedEventData {
    masterArticleNo: number;
    colorCode: number;
}

export interface IBreadcrumbData {
    category: ICategory;
}

export interface ICategory {
    navigationKey: string;
    navigationKeyPath: string;
    seoSlug: string;
    name: string;
}

export interface IDeliveryInformation {
    maxValue: number;
    minValue: number;
    periodType: number;
    calendarWeek: number;
}

export interface IMixedMedia {
    flexibleSetImageFullPath: string;
    sortedMixedMediaForBricks: IMixedMediaOfBrick[];
    altTagForFlexibleSetImage: string;
}

export interface IMixedMediaOfBrick {
    brickNo: number;
    selectedBrickItemNo: number;
    altTagForImages: string;
    masterArticleNo: number;
    colorCode: number;
    isFreeInSet: boolean;
    linkToDetailsPage: string;
    imageFullPath?: string;
    mainActionImageFullPath?: string;
    additionalInfoImagesFullPaths: string[];
    shoeSoleImageFullPath?: string;
    secondaryImageFullPath?: string;
    detailedImageFullPath?: string;
    includedQuantity: number;
}

export interface IOrderModuleData {
    flexSetInfo: IFlexSetInfo;
    flexSetPrice: IFlexSetPrice;
    availabilityState?: number;
    deliveryInformation: IDeliveryInformation;
    maxQuantityPerOrder: number;
    shareButtonUrl: string;
    countryDependentContents: ICountryDependentContentsModel;
    sortedBrickSelections: IBrickSelection[];
    flexibleSetCount: number;
}

export interface ICountryDependentContentsModel {
    defaultCountryDependentContentsText: string;
    countryDependentContentsAttributeValue: string;
}

export interface IFlexSetInfo {
    id: string;
    fsCode: string;
    displayName: string;
    //packagingInformation: IPackagingInformation;
    isRemainingStock: boolean;
}

/*
export interface IPackagingInformation {
    packageSize: number;
    quantityUnit: IQuantityUnit;
    subQuantityUnit?: IQuantityUnit;
}*/

export interface IQuantityUnit {
    isoCode: string;
    plural: string;
    quantityUnitCode: number;
    singular: string;
}

export interface IFlexSetPrice {
    price: IPrice;
    currency: ICurrency;
    originalPrice: IPrice;
    discountType: number;
    hasRecommendedRetailPrice: boolean;
    isSale: boolean;
    priceDeduction?:number;
}

export interface IPrice {
    grossValue: number;
    netValue: number;
    basePrice?: IBasePrice;
}

export interface IBasePrice extends IPrice {
    quantity: number;
    unit: string;
}

export interface ICurrency {
    isoCode: string;
    symbol: string;
    symbolShortForm: string;
    prependCurrencySymbol: boolean;
}

export interface IScalePrice {
    unit: string;
    price: IPrice;
    quantity: number;
}

export interface IColor {
    code: number;
    name: string;
}

export interface IAvailableColor extends IColor {
    iconPath: string;
    defaultArticleImagePath: string;
    salesArticleVariantKey: string;
}

export interface IModel {
    code: number;
    label: string;
}

export interface ITranslationUrls {
    culture: string;
    url: string;
}

export interface IAvailableModel extends IModel {
    salesArticleVariantKey: string;
    defaultArticleImagePath: string;
}

export interface ISizeType {
    sizeTypeCode: number;
    sizeTypeName: string;
    sizes: ISalesArticleVariantSize[];
}

export interface ISalesArticleVariantSize {
    salesArticleVariantKey: string;
    size: ISize;
}

export interface ISize {
    code: number;
    fittingAccuracy?: number;
    label: string;
    name: string;
}

export interface IShutterModule {
    productInfo: IProductInfo;
    layoutedContentGridKeys: string[];
    emotionalText: string;
}

export interface IProductInfo {
    description: IProductDescription; // Description tab
    ImageFullPath: string;
}

export interface IDisclaimer {
    name: string;
    structuredText: IRichTextReference;
    rtfText: string | null;
    dataMode: number;
    globalRank: number;
}

export interface IRichTextReference {
    id: string;
    items: ITextItem[];
}

export interface ITextItem {
    type: string;
    children: ITextItemChild[];
}

export interface ITextItemChild {
    text: string;
    markups: string[];
    children: IFormattedText[];
}

export interface IFormattedText {
    text: string;
    markups: string[];
}

export interface ILogo {
    imageUrl: string;
    altText: string;
}

export interface IProductDescription {
    sortedDisclaimers: IDisclaimer[];
    sortedArticleSetItemDescriptions: IArticleSetItem[];
    isSmakeDesignerAvailable: boolean;
}

export interface IArticleSetItem {
    colorText: string;
    modelText: string;
    sizeText: string;
    isSellableSeparately: boolean;
    linkToDetailsPage: string;
    includedQuantity: number; //IncludedQuantity
    quantity: number;
    isFreeInSet: boolean; //IsFreeInSet 
    masterArticleNo: number;
    salesArticleVariantKey: string;
    salesDesignation: string;
    brickNo: number;
    selectedBrickItemNo: number;
    modelCode: number;
}

export interface ISpecificationEntry {
    name: string;
    sortedValues: string[];
}

export interface IContextData {
    culture: string;
    portal: string;
    portalMandantToken: string;
    apiLink: string;
    originalUrl: string;
    baseUrlForAssets: string;
    actualRequestCookies: { key: string; value: string }[];
    customerData: ICustomerData;
    isMobile: boolean;
    isEsApp: boolean;
    priceMode?: 'Gross' | 'Net';
    wwsId: string;
    isRelease: boolean;
    featureToggles: IFeatureToggles;
    numberFormat: INumberFormat;
    originalRootPath: string;
}

export interface IFeatureToggles {
    redesignOrderModal: boolean;
    enableLogoCreator: boolean;
    piwikProEnableEcommerceTracking: boolean;
    enablePersonalizationBannerOnDetailsPage: boolean;
    enableMetaPixel: boolean;
    enableTikTokPixel: boolean;
    enableKraken: boolean;
    disableDirectEcommerceTracking: boolean;
    enableClientLogging: boolean;
}

export interface INumberFormat {
    numberDecimalSeparator: string;
    numberGroupSeparator: string;
}

export interface ICustomerData {
    ssoId: string;
    customerNumbers: [number];
    hasCustomerPrices?: boolean;
    priceLevels: [];
    flagVatFree?: boolean;
    postageFree?: string;
}


export enum SalesArticleType {
    NormalArticle,
    VoucherArticle,
    ArticleSet,
}

export enum VariantType {
    Color,
    Size,
    Special,
    Model,
    None,
    Kiosk, // special type for kiosk only. If we have color, show color panel. Otherwise, show size panel
}

export enum AvailabilityStatusEnum {
    InStock,
    CurrentlyOutOfStock,
    NotInStock,
}
